import _ from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import classNames from 'clsx';

import { Checkbox, FormControlLabel, Select, FormControl, MenuItem, IconButton } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';

import { SPECIAL_REQUEST_SUGGESTIONS } from 'arbitrip-common/general/constants';
import ReservationActions from '@/actions/ReservationActions';

import CheckboxActiveIcon from '@/components/pages/results/filters/components/CheckboxActiveIcon';
import CheckboxInactiveIcon from '@/components/pages/results/filters/components/CheckboxInactiveIcon';
import { ReactComponent as InfoIcon } from '@/img/reservation_page/info.svg';

const SpecialRequests = ({ alwaysShowSpecialRequests, isChecked, handleChecked, reservationData, shouldLockForm }) => {
	const { t, i18n } = useTranslation();

	const handleTextAreaChange = (e) => {
		ReservationActions.updateSpecialRequests(e.target.value);
	};

	const handleSelect = (e, requestKey) => {
		const optionKey = e.target.value;
		const options = SPECIAL_REQUEST_SUGGESTIONS[requestKey].options;
		const selectedOption = options.find((o) => o.key === optionKey);

		if (selectedOption) {
			const suggestionSpecialRequest = _.get(reservationData, 'specialRequestsFromSuggestions', {});
			ReservationActions.updateSpecialRequestsFromSuggestions({
				...suggestionSpecialRequest,
				[requestKey]: { key: optionKey, text: getTagText(selectedOption, requestKey) },
			});
		}
	};

	const clearSelection = (requestKey) => {
		const suggestionSpecialRequest = _.get(reservationData, 'specialRequestsFromSuggestions');
		delete suggestionSpecialRequest[requestKey];

		ReservationActions.updateSpecialRequestsFromSuggestions(suggestionSpecialRequest);
	};

	const transformOptionText = (option, requestKey) => {
		if (requestKey === 'ARRIVING_LATE') {
			const checkInDate = _.get(reservationData, 'deal.details.check_in');
			const nextDate = checkInDate ? checkInDate.clone().add(1, 'd') : checkInDate;
			return option.addNextDate && nextDate
				? `${option.textValue} (${nextDate.locale(i18n.language).format('Do MMM')})`
				: option.textValue;
		}
		return option.textValue;
	};

	const getTagText = (option, requestKey) => {
		if (requestKey === 'ARRIVING_LATE') {
			return `${t('SpecialRequestsList.ArrivingLateBetween')} ${transformOptionText(option, requestKey)}`;
		}
		return option.textValue;
	};

	const getArrivingLateSelect = () => {
		const arrivingLateSelect = Object.keys(SPECIAL_REQUEST_SUGGESTIONS).map((key) => {
			const request = SPECIAL_REQUEST_SUGGESTIONS[key];
			const selectedRequest = reservationData?.specialRequestsFromSuggestions[key] || null;

			return (
				<div className="select-container" key={key}>
					<div className={`subtitle ${selectedRequest ? 'disabled' : ''}`}>{t(request.dialogText)}</div>
					<FormControl size="small" disabled={!!selectedRequest || shouldLockForm} sx={{ width: 380 }}>
						<Select
							value={selectedRequest?.key || ''}
							onChange={(e) => handleSelect(e, key)}
							displayEmpty
							aria-label={t(request.dialogText)}
						>
							<MenuItem value="" disabled hidden>
								{t(request.selectPlaceHolder)}
							</MenuItem>
							{request.options.map((option) => (
								<MenuItem value={option.key} key={option.key}>
									{transformOptionText(option, key)}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{alwaysShowSpecialRequests && (
						<div className="arriving-late-message">
							<InfoIcon />
							<div className="arriving-late-text">{t('SpecialRequestsList.ArrivingLateMessage')}</div>
						</div>
					)}
				</div>
			);
		});

		return arrivingLateSelect;
	};

	return (
		<div className={classNames('special-requests', { disabled: shouldLockForm })}>
			<div className="title">{t('SpecialRequestsList.NotesToHotel')}</div>
			{!alwaysShowSpecialRequests && (
				<div className="checkbox-container">
					<FormControlLabel
						control={
							<Checkbox
								disabled={shouldLockForm}
								checked={isChecked}
								onChange={handleChecked}
								icon={<CheckboxInactiveIcon strokeColor={shouldLockForm && '#BCC0C0'}/>}
								checkedIcon={<CheckboxActiveIcon fillColor={shouldLockForm && '#BCC0C0'}/>}
							/>
						}
						label={t('SpecialRequestsList.HaveSpecialRequest')}
					/>
				</div>
			)}
			{(isChecked || alwaysShowSpecialRequests) && (
				<div>
					<div className="disclaimer">
						<Trans i18nKey="SpecialRequestsCantBeGuaranteed" components={{ bold: <strong className="semi-bold" /> }} ></Trans>
					</div>
					{alwaysShowSpecialRequests && getArrivingLateSelect()}
					<div className={`textarea-container ${alwaysShowSpecialRequests ? 'expanded' : ''}`}>
						<label className="subtitle" htmlFor="special-requests-input">
							{t('SpecialRequestsList.AddSpecialRequest')}
						</label>
						<div className="textarea-wrap">
							<textarea
								id="special-requests-input"
								dir="ltr"
								className={
									!_.isEmpty(reservationData?.specialRequestsFromSuggestions) ? 'expanded' : ''
								}
								disabled={shouldLockForm}
								placeholder={t('SpecialRequestsList.WriteHere')}
								value={reservationData.specialRequests}
								onChange={handleTextAreaChange}
							/>
							{!_.isEmpty(reservationData?.specialRequestsFromSuggestions) && (
								<div className="tags-wrap">
									{Object.keys(reservationData.specialRequestsFromSuggestions).map((key) => {
										const specialRequest = reservationData.specialRequestsFromSuggestions[key];
										return (
											<div key={key} disabled={shouldLockForm} className="tag">
												<span>{specialRequest.text}</span>
												<IconButton
													className="clear-selection-button"
													aria-label="clear selection"
													onClick={() => clearSelection(key)}
												>
													<CloseOutlined />
												</IconButton>
											</div>
										);
									})}
								</div>
							)}
						</div>
					</div>
					{!alwaysShowSpecialRequests && getArrivingLateSelect()}
				</div>
			)}
		</div>
	);
};

export default SpecialRequests;
